import { meterToMiles } from "config/utils"
import { Table2, Icon } from "components"
import useAppSelector from "hooks/useAppSelector"
import useAppDispatch from "hooks/useAppDispatch"
import {
	isNearbyEmployeesLoading,
	selectNearbyLocationEmployees,
	getEmployeesNearbyLocation,
} from "store/Geolocation/geolocation.slice"
import { useContext, useMemo, useState, useEffect } from "react"
import EmployeeRetainContext, { EmployeeRetainContextProps } from "pages/schedules/Location/CreateScheduleRetainContext"
import { EmployeeTypesEnum } from "config/constants"
import AntdModal from "./AntdModal"
import { selectBranchesMappedAsOptions } from "store/Branches/branch.slice"

interface Props {
	open: boolean
	canSelect?: boolean
	reset?: () => void
	modalHandler: (open: boolean) => void
	location?: any
	filterEmployeeType?: string
}

const NearbyEmployeesModal: React.FC<Props> = ({
	open,
	modalHandler,
	location,
	reset,
	filterEmployeeType,
	canSelect = false,
}) => {
	const dispatch = useAppDispatch()
	const nearbyEmployees = useAppSelector(selectNearbyLocationEmployees(location?._id))
	const nearbyEmployeesLoading = useAppSelector(isNearbyEmployeesLoading())
	const branchesMap = useAppSelector(selectBranchesMappedAsOptions())
	const [selectedRow, setSelectedRow] = useState("")
	const [currentPage, setCurrentPage] = useState(0)
	const [hasMore, setHasMore] = useState(true)
	const pageSize = 10
	const { handleCreateScheduleModalData, createScheduleModalData } = useContext(
		EmployeeRetainContext,
	) as EmployeeRetainContextProps

	const filteredEmployeeByPostType = useMemo(() => {
		return !filterEmployeeType
			? nearbyEmployees.map(employee => ({
					...employee,
					branch: branchesMap.find(branch => branch.value === employee?.branch)?.label,
			  }))
			: nearbyEmployees
					.filter(employee => {
						if (filterEmployeeType === EmployeeTypesEnum.SECURITY || filterEmployeeType === "") {
							return !employee?.type || employee?.type === EmployeeTypesEnum.SECURITY
						} else return employee?.type === EmployeeTypesEnum.CLEANING
					})
					.map(employee => ({
						...employee,
						branch: branchesMap.find(branch => branch.value === employee?.branch)?.label,
					}))
	}, [filterEmployeeType, nearbyEmployees])

	const selectHandler = () => {
		if (reset) {
			reset()
			handleCreateScheduleModalData({
				...createScheduleModalData,
				branch: "",
				supplier: "",
				...(!createScheduleModalData?.checkMultiple ? { employee: selectedRow } : {}),
			})
			modalHandler(false)
		}
	}

	// Track data changes to detect when there's no more data
	useEffect(() => {
		if (currentPage > 0 && !nearbyEmployeesLoading) {
			const expectedTotalItems = currentPage * pageSize
			const actualTotalItems = nearbyEmployees.length
			const lastResponseSize = actualTotalItems - expectedTotalItems

			if (lastResponseSize < pageSize) {
				setHasMore(false)
			}
		}
	}, [nearbyEmployees, currentPage, nearbyEmployeesLoading, pageSize])

	const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
		const { scrollTop, scrollHeight, clientHeight } = event.currentTarget
		if (scrollTop + clientHeight >= scrollHeight * 0.8 && !nearbyEmployeesLoading && hasMore) {
			const nextPage = currentPage + 1
			const offset = nextPage * pageSize
			dispatch(
				// TODO: verify changes
				// getEmployeesNearbyLocation(location?._id, filterEmployeeType ?? ScheduleTypesEnum.DUTY, {
				// 	limit: pageSize,
				// 	offset,
				// }),
				getEmployeesNearbyLocation(location?._id, filterEmployeeType ?? "DUTY", {
					limit: pageSize,
					offset,
				}),
			)
			setCurrentPage(nextPage)
		}
	}

	// Reset pagination when modal opens
	useEffect(() => {
		if (open) {
			setCurrentPage(0)
			setHasMore(true)
		}
	}, [open])

	return (
		<AntdModal
			open={open}
			title={"Nearby Employees (within 50 miles)"}
			onCancel={() => modalHandler(false)}
			okText="Select"
			onOk={selectHandler}
			okButtonProps={{ disabled: !selectedRow, hidden: !canSelect }}
			cancelButtonProps={{ hidden: !canSelect }}
		>
			<div>
				<div className="font-semi pt-1 pb-2 pl-2 text-md font-semibold">{`Location: ${location?.name}`}</div>
				{nearbyEmployeesLoading && nearbyEmployees.length === 0 ? (
					<div className={`flex min-h-52 w-full max-w-3xl items-center justify-center  overflow-hidden`}>
						<Icon name="spinner" color="black" size={85} className=" animate-spin" />
					</div>
				) : (
					<div className="h-full flex-col space-y-8">
						<div className="h-[50vh] overflow-y-auto" onScroll={handleScroll}>
							<Table2
								headerRowClassName="sticky top-0"
								selectedRows={[selectedRow]}
								rowClassName={"cursor-pointer"}
								onRowClick={setSelectedRow}
								selectRow={true}
								selectedRowColor="#c4e5f8"
								rows={filteredEmployeeByPostType}
								loading={nearbyEmployeesLoading && currentPage > 0}
								columns={[
									{
										label: "Employee",
										key: "firstName",
										render: ({ row, key }: any) => <div> {`${row[key]} ${row["lastName"]}`}</div>,
									},

									{
										label: "Branch",
										key: "branch",
										render: ({ row, key }: any) => <div>{row[key]}</div>,
									},
									{
										label: "Distance",
										key: "nearbyDistance",
										render: ({ row, key }: any) => (
											<div>{`${meterToMiles(row[key]["value"])} mi`}</div>
										),
									},
									{
										label: "Duration",
										key: "nearbyDistanceDuration",
										render: ({ row, key }) => <div> {row[key]["text"]}</div>,
									},
								]}
							/>
							{!hasMore && currentPage > 0 && (
								<div className="flex justify-center py-4">
									<span className="text-sm text-gray-500">No more employees to show</span>
								</div>
							)}
						</div>
					</div>
				)}
			</div>
		</AntdModal>
	)
}

export default NearbyEmployeesModal
